import './secondary-attribute-message-modal.scss'
import { OverrideCmsImage } from '@cms/components/cmsImage'
import type { OverrideCmsImageProps } from '@cms/components/cmsImage/OverrideCmsImage'
import type { CmsImageProps } from '@omnia/ui/src/lib/cms/components/cmsImage/CmsImage'
import type { CroppedImage } from '@omnia/ui/src/lib/cms/types/croppedImage'
import { useLogger } from '@omnia/ui/src/lib/hooks/useLogger'
import { Modal } from '@vista-digital/occ-ui'
import { useUnmountEffect } from '@vista-digital/react-tools'
import { observe } from 'mobx'
import { type FC, useState } from 'react'
import { navigationService } from '@/content/services/navigation.service'
import type SecondaryAttributeMessagePropsApi from './models'

interface SecondaryAttributeModalProps extends SecondaryAttributeMessagePropsApi {
    onClose?: VoidFunction
}

export const SecondaryAttributeModal: FC<SecondaryAttributeModalProps> = props => {
    const [open, setOpen] = useState(true)
    const logger = useLogger('SecondaryAttributeModal')

    logger.trace('Rendered')

    // Secondary attribute message modals can have a completely different image for mobile devices
    const mixedImage: CroppedImage = {
        alt: props?.secondaryAttributePricingModalMediaDesktop?.alt ?? '',
        desktop: props?.secondaryAttributePricingModalMediaDesktop?.desktop ?? '',
        tablet: props?.secondaryAttributePricingModalMediaDesktop?.tablet ?? '',
        mobile: props?.secondaryAttributePricingModalMediaMobile?.mobile ?? '',
    }

    const onClose = () => {
        logger.debug('Closing')

        setOpen(false)
        props?.onClose?.()
    }

    // As this modal is created outside the normal render tree, it is not immediately destroyed when its 'parent' is.
    // Therefore, we'll listen to the page observer to detect if the page re-renders or the user somehow navigates while this modal is open
    const unobserve = observe(navigationService.pageObs, () => {
        logger.debug('Page change detected when secondary attribute modal was open.')
        onClose()
    })

    useUnmountEffect(() => {
        unobserve()
    })

    const cmsImageProps: CmsImageProps = {
        className: 'secondary-attribute-message-modal__image',
        image: mixedImage,
    }

    const overrideCmsImageProps: OverrideCmsImageProps = {
        dimensions: {
            mobile: { width: 0 },
            tablet: { width: 0 },
            desktop: { width: 0 },
        },
        cmsImageProps,
    }

    return (
        <Modal
            show={open}
            canBeClosed={true}
            onClose={onClose}
            heading={props.secondaryAttributePricingModalTitle}
            className="secondary-attribute-message-modal v-modal--generic"
        >
            <OverrideCmsImage {...overrideCmsImageProps} />
        </Modal>
    )
}
